<mat-sidenav-container class="sidenav-container" [hasBackdrop]="true">
  <mat-sidenav #filterSidenav position="end" mode="over" [fixedInViewport]="true" [fixedTopGap]="0" [fixedBottomGap]="0">
    <app-permit-filter-sidenav
      #filterSidenavCmp
      [savedFilters]="savedFilters"
      (filterChanged)="onFilterChanged($event)"
      (saveFilter)="onSaveFilter($event)"
      (deleteFilter)="onDeleteFilter($event)"
      (loadFilter)="onLoadFilter($event)"
      (close)="filterSidenav.close()">
    </app-permit-filter-sidenav>
  </mat-sidenav>

  <mat-sidenav-content>
    <div class="view-controls">
      <div class="view-tabs">
        <div class="tab" [class.active]="activeView === 'list'" (click)="toggleView('list')">{{ 'permit.manage.list.view_list' | translate }}</div>
        <div class="tab" [class.active]="activeView === 'map'" (click)="toggleView('map')">{{ 'permit.manage.list.view_map' | translate }}</div>
      </div>
      <div class="filter-export-buttons">
        <div class="search-container">
          <mat-icon class="search-icon">search</mat-icon>
          <input
            type="text"
            [formControl]="searchControl"
            [placeholder]="'permit.manage.list.search_placeholder' | translate"
            class="search-input"
          >
          <mat-icon class="clear-icon" *ngIf="searchControl.value" (click)="clearSearch()">close</mat-icon>
        </div>

        <button
          mat-button
          class="filter-button custom-border-button custom-font-normal"
          (click)="filterSidenav.toggle()"
        >
          {{ 'permit.manage.list.filters' | translate }}
          <span *ngIf="filterSidenavCmp?.activeFilterCount > 0" class="filter-count-badge">
            {{ filterSidenavCmp?.activeFilterCount }}
          </span>
        </button>

        <button class="create-permit-button" (click)="openCreatePermitDialog()">
          {{ 'permit.create.list_button' | translate }}
        </button>
      </div>
    </div>

    <div class="content-container">
      <div *ngIf="loading" class="loading-skeleton">
        <app-skeleton-loader *ngFor="let i of [1, 2, 3, 4, 5]" height="52px" borderRadius="8px"></app-skeleton-loader>
      </div>

      <div class="map-container" *ngIf="activeView === 'map'">
        <div id="permit-list-map"></div>
        <div class="map-empty-hint" *ngIf="!loading && getMappablePermits().length === 0">
          {{ 'permit.manage.list.map_no_location' | translate }}
        </div>
      </div>

      <div *ngIf="activeView === 'list' && !loading && permits.length === 0" class="no-permits">
        <h2>{{ 'permit.manage.list.empty' | translate }}</h2>
      </div>

      <div *ngIf="activeView === 'list' && !loading && permits.length > 0" class="permits-table">
        <div class="permit-title-container">
          <span class="permit-title">{{ 'permit.manage.list.list_title' | translate }}</span>
          <span class="permit-count">{{ total }}</span>
        </div>

        <table>
          <thead>
            <tr>
              <th *ngFor="let header of columnHeaders"
                  [class.sortable]="header.sortable"
                  (click)="header.sortable && onSortHeader(header.sortField)">
                <div class="header-content">
                  {{ header.translationKey | translate }}
                  <span *ngIf="header.sortable" class="sort-icon">
                    <mat-icon style="font-size: 18px; height: 18px; width: 18px; vertical-align: middle;">
                      {{ getSortIcon(header) }}
                    </mat-icon>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let permit of permits"
                (click)="openPermitDetail(permit)"
                class="permit-row">

              <!-- Type column: category icon + type name (no unread dot in Slice 1) -->
              <td class="type-cell">
                <div class="type-with-icon">
                  <div class="type-icon">
                    <i [ngClass]="categoryIcon(permit)"></i>
                  </div>
                  <div class="type-info">
                    <span class="type-name">{{ localizedName(permit.permitType?.name) }}</span>
                  </div>
                </div>
              </td>

              <!-- Category column -->
              <td class="category-cell">{{ localizedName(permit.category?.name) }}</td>

              <!-- PermitId + sync badge -->
              <td class="permit-id-cell">
                <div class="permit-id-wrapper">
                  <span>{{ permit.permitId }}</span>
                  <span
                    *ngIf="permit.syncStatus"
                    class="sync-dot"
                    [ngStyle]="{ 'background-color': syncColorMap[permit.syncStatus] }"
                    [matTooltip]="syncTooltipMap[permit.syncStatus] | translate">
                  </span>
                </div>
                <div class="external-number" *ngIf="permit.externalPermitNumber">
                  {{ 'permit.manage.list.external_number' | translate }} {{ permit.externalPermitNumber }}
                </div>
              </td>

              <!-- Location -->
              <td class="location-cell">
                <div class="location-wrapper" *ngIf="getLocationText(permit)">
                  <span>{{ getLocationText(permit) }}</span>
                  <mat-icon class="location-pin-icon" style="font-size: 16px; height: 16px; width: 16px;">location_on</mat-icon>
                </div>
                <span *ngIf="!getLocationText(permit)" class="no-location">—</span>
              </td>

              <!-- Created date -->
              <td class="date-cell">{{ formatDate(permit.createdAt) }}</td>

              <!-- Status -->
              <td class="status-cell" (click)="$event.stopPropagation()">
                <app-permit-status-select [permit]="permit" (refreshRequested)="loadPermits()"></app-permit-status-select>
              </td>

              <!-- Department chip -->
              <td class="department-cell">
                <div *ngIf="permit.department" class="department-badge"
                     [ngStyle]="{ 'background-color': getDepartmentChipColor(), 'color': DEPARTMENT_DEFAULT_COLOR }">
                  <span class="department-text">{{ localizedName(permit.department.name) }}</span>
                </div>
                <div *ngIf="!permit.department" class="department-badge department-unassigned">
                  <span class="department-text">—</span>
                </div>
              </td>

              <!-- Tags -->
              <td class="tags-cell">
                <div class="tags-container" *ngIf="permit.tags?.length">
                  <span class="tag-badge" *ngFor="let tag of permit.tags | slice:0:3">{{ tag }}</span>
                  <span class="tag-more" *ngIf="permit.tags.length > 3">+{{ permit.tags.length - 3 }}</span>
                </div>
              </td>

              <!-- Applicant -->
              <td class="applicant-cell">
                <div class="applicant-container" *ngIf="permit.applicant">
                  <app-avatar
                    [picture]="applicantPicture(permit)"
                    [initials]="applicantPicture(permit) ? '' : applicantInitials(permit)"
                    [size]="24">
                  </app-avatar>
                  <span class="applicant-name">{{ applicantName(permit) }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <mat-paginator
        *ngIf="!loading && permits.length > 0"
        [length]="total"
        [pageSize]="pageSize"
        [pageSizeOptions]="pageSizeOptions"
        [pageIndex]="pageIndex"
        (page)="onPageChange($event)"
        showFirstLastButtons>
      </mat-paginator>
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>
<router-outlet></router-outlet>
